import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Wifi,
  WifiOff,
  MoreVertical,
  CirclePlus,
  Power,
  RefreshCw,
  FileText,
  SquarePen,
  Trash2,
} from "lucide-react";
import { AuthContext } from "../../Context/AuthContext";
import api from "../../api/axios";
import Swal from "sweetalert2";

const DeviceManagement = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncPinging, setSyncPinging] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);

  const [formData, setFormData] = useState({
    deviceName: "",
    serialNumber: "",
    ipAddress: "",
    port: "",
    esslUser: "",
    esslPass: "",
  });

  const { user } = useContext(AuthContext);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddDevice = async (e) => {
    e.preventDefault();
    if (!formData.deviceName || !formData.serialNumber || !formData.ipAddress) {
      alert("Please fill all required fields.");
      return;
    }
    setLoading(true);
    const newDevice = {
      device_name: formData.deviceName,
      serial_number: formData.serialNumber,
      ip_address: formData.ipAddress,
      port: formData.port,
      username: formData.esslUser,
      password: formData.esslPass,
      location: formData.location,
      status: "offline",
    };

    try {
      const res = await api.post(
        `/hrms/devices?property_code=${user.property_code}`,
        newDevice
      );

      if (res.status === 200 || res.status === 201) {
        setDevices([...devices, newDevice]);
        setFormData({
          deviceName: "",
          serialNumber: "",
          ipAddress: "",
          port: "",
          esslUser: "",
          esslPass: "",
          location: "",
        });
        Swal.fire("Success", "Device added successfully!", "success");
        fetchDevices();
      }
    } catch (error) {
      Swal.fire("Error", "Failed to add Device.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDevices(devices.filter((device) => device.id !== id));
  };

  const handleSyncLogs = async (device) => {
    try {
      Swal.fire({
        title: "Syncing...",
        text: "Please wait while syncing logs.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
      setSyncLoading(true);
      const res = await api.post(
        `/hrms/devices/${device.id}/sync?property_code${device.property_code}`
      );
      Swal.fire(
        "Success",
        `Logs synced successfully for device ID: ${device.device_name}`,
        "success"
      );
    } catch (error) {
      Swal.fire("Error", "Failed to sync logs", "error");
    } finally {
      fetchDevices();
      setSyncLoading(false);
    }
  };

  const handleOldSyncSubmit = async () => {
    if (!fromDate || !toDate) {
      alert("Please select both dates");
      return;
    }
    setShowSyncModal(false);

    try {
      Swal.fire({
        title: "Syncing...",
        text: "Please wait while syncing logs.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      setSyncLoading(true);

      await api.post(`/hrms/devices/${selectedDevice.id}/sync-old-logs`, {
        from_date: fromDate,
        to_date: toDate,
      });

      Swal.fire("Success!", "Logs synced successfully.", "success");
      setFromDate("");
      setToDate("");
    } catch (err) {
      Swal.fire("Error", "Failed to sync logs", "error");
    } finally {
      setSyncLoading(false);
      setShowSyncModal(false);
    }
  };

  const handlePing = async (device) => {
    try {
      Swal.fire({
        title: "Pinging...",
        text: "Please wait while pinging the device.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      setSyncPinging(true);

      const res = await api.post(
        `/hrms/devices/${device.id}/ping?property_code=${device.property_code}`
      );
      if (res.data?.success === false) {
        Swal.fire("Error", res.data?.message || "Ping failed", "error");
        return;
      }

      Swal.fire(
        "Success",
        res.data?.message ||
          `Device pinged successfully: ${device.device_name}`,
        "success"
      );
    } catch (error) {
      Swal.fire("Error", error?.message || "Failed to ping device", "error");
    } finally {
      fetchDevices();
      setSyncPinging(false);
    }
  };

  const fetchDevices = useCallback(async () => {
    try {
      const res = await api.get(
        `/hrms/devices?property_code=${user.property_code}`
      );
      const data = res.data || [];
      setDevices(data.data);
    } catch (error) {
      console.error("Failed to fetch shifts:", error);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
      <div className="row">
        <div className="col-12">
          <div className="hrms-device-management-cont">
            <div className="d-flex flex-column gap-1">
              <h5 className="fs-4 fw-semibold m-0">
                Register New Biometric Device
              </h5>
              <span className="fs-6 text-muted">
                Add a new device to this property.
              </span>
            </div>

            <form onSubmit={handleAddDevice} className="mt-4">
              <div className="row">
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <label className="form-label">Device Name</label>
                  <input
                    type="text"
                    name="deviceName"
                    value={formData.deviceName}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g., Main Entrance"
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <label className="form-label">Serial Number</label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={formData.serialNumber}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g., C072K12345"
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <label className="form-label">IP Address</label>
                  <input
                    type="text"
                    name="ipAddress"
                    value={formData.ipAddress}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g., 192.168.1.100"
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <label className="form-label">Port</label>
                  <input
                    type="number"
                    name="port"
                    value={formData.port}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g., 4370"
                  />
                </div>
                <div className="col-lg-3 col-md-6 col-sm-12 col-12">
                  <label className="form-label">ESSL Username</label>
                  <input
                    type="text"
                    name="esslUser"
                    value={formData.esslUser}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Default: Edureka"
                  />
                </div>
                <div className="col-lg-3 col-md-6  col-sm-12 col-12">
                  <label className="form-label">ESSL Password</label>
                  <input
                    type="password"
                    name="esslPass"
                    value={formData.esslPass}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Default: Edureka@123"
                  />
                </div>
                <div className="col-lg-3 col-md-6  col-sm-12 col-12">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="Default: Chennai"
                  />
                </div>
              </div>
              <button
                className="btn device-mang-btn d-flex align-items-center gap-2 py-2 px-3 mt-4"
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <CirclePlus size={18} stroke="#fff" />
                    Add Device
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="table-responsive hrms-device-management-table mt-4">
            <table className="table table-hover m-0">
              <thead>
                <tr className="border-top">
                  <th>Device Name</th>
                  <th>Serial Number</th>
                  <th>IP Address</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.length > 0 ? (
                  devices.map((device) => (
                    <tr key={device.id}>
                      <td className="fw-semibold">{device.device_name}</td>
                      <td>{device.serial_number}</td>
                      <td>{device.ip_address}</td>
                      <td>
                        {device.status === "online" ? (
                          <span className="text-success d-flex align-items-center gap-1">
                            <Wifi size={16} /> Online
                          </span>
                        ) : (
                          <span className="text-danger d-flex align-items-center gap-1">
                            <WifiOff size={16} /> Offline
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="dropdown">
                          <MoreVertical
                            size={16}
                            className="dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            style={{ cursor: "pointer" }}
                          />
                          <ul className="dropdown-menu">
                            <li>
                              <div
                                className="dropdown-item d-flex align-items-center gap-2"
                                style={{
                                  cursor: syncPinging
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                onClick={
                                  !syncPinging
                                    ? () => handlePing(device)
                                    : undefined
                                }
                              >
                                {syncPinging ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    <span>Ping...</span>
                                  </>
                                ) : (
                                  <>
                                    <Power size={18} />
                                    Ping Device
                                  </>
                                )}
                              </div>
                            </li>
                            <li>
                              <div
                                className="dropdown-item d-flex align-items-center gap-2"
                                style={{
                                  cursor: syncLoading
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                onClick={
                                  !syncLoading
                                    ? () => handleSyncLogs(device)
                                    : undefined
                                }
                              >
                                {syncLoading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    <span>Syncing...</span>
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw size={18} />
                                    Sync Logs
                                  </>
                                )}
                              </div>
                            </li>
                            <li>
                              <div
                                className="dropdown-item d-flex align-items-center gap-2"
                                style={{
                                  cursor: syncLoading
                                    ? "not-allowed"
                                    : "pointer",
                                }}
                                onClick={() => {
                                  setSelectedDevice(device);
                                  setShowSyncModal(true);
                                }}
                              >
                                {syncLoading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    <span>Syncing...</span>
                                  </>
                                ) : (
                                  <>
                                    <RefreshCw size={18} />
                                    Sync old Logs
                                  </>
                                )}
                              </div>
                            </li>
                            <li>
                              <div
                                className="dropdown-item d-flex align-items-center gap-2"
                                style={{ cursor: "pointer" }}
                              >
                                <FileText size={18} />
                                View Logs
                              </div>
                            </li>
                            <li className="border-top border-bottom py-1">
                              <div
                                className="dropdown-item d-flex align-items-center gap-2"
                                style={{ cursor: "pointer" }}
                              >
                                <SquarePen size={18} />
                                Edit
                              </div>
                            </li>
                            <li>
                              <div
                                className="dropdown-item text-danger d-flex align-items-center gap-2"
                                onClick={() => handleDelete(device.id)}
                                style={{ cursor: "pointer" }}
                              >
                                <Trash2 size={18} />
                                Delete
                              </div>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Loading Devices...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {showSyncModal && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.4)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 9999,
              }}
            >
              <div
                style={{
                  background: "#fff",
                  padding: "30px",
                  borderRadius: "12px",
                  width: "450px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                <h2
                  style={{
                    textAlign: "center",
                    marginBottom: "25px",
                  }}
                >
                  Sync Old Logs
                </h2>

                {/* From Date */}
                <div style={{ marginBottom: "20px" }}>
                  <label>From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="form-control"
                    placeholder="dd-mm-yyyy"
                  />
                </div>

                {/* To Date */}
                <div style={{ marginBottom: "25px" }}>
                  <label>To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="form-control"
                    placeholder="dd-mm-yyyy"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                  }}
                >
                  <button
                    style={{
                      background: "#7066ff",
                      color: "#fff",
                      border: "none",
                      padding: "10px 25px",
                      borderRadius: "6px",
                    }}
                    onClick={async () => {
                      await handleOldSyncSubmit();
                    }}
                  >
                    Sync
                  </button>

                  <button
                    style={{
                      background: "#6c757d",
                      color: "#fff",
                      border: "none",
                      padding: "10px 25px",
                      borderRadius: "6px",
                    }}
                    onClick={() => setShowSyncModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceManagement;
